import { useEffect, useMemo, useState } from "react";
import { ALL_FRAME_TYPES } from "../hooks/useSharedParams";
import { splitSelection, togglePieSelection } from "../utils/pieSelection";
import type { DistributionItem, Stats } from "../types";
import { colourMatchKey, normalizeColourSpelling } from "../utils/colourLabels";
import { COMBO_QUANTITY_COL, SHARE_PCT_COL } from "../utils/combinationListTooltips";
import { ComboDescription } from "./ComboDescription";
import { DistributionChart } from "./DistributionChart";
import { HelpHint } from "./HelpHint";
import styles from "./FramesDetails.module.css";
import { Pagination } from "./Pagination";

/** Regular click = exclusive; ⌘-click or long-press = multi-select (from none: all except this). */
function toggleInSet(current: string | undefined, value: string, allValues: string[], multiSelect?: boolean): string {
    return togglePieSelection(splitSelection(current), value, allValues, !!multiSelect).join(",");
}

const colourValue = (label: string) => (label === "Mixed" ? "mixed" : label);
const cmValue = (label: string) => (label === "Mixed" ? "mixed" : label.replace("cm", ""));

interface FramesDetailsProps {
    stats: Stats;
    widthDistribution: DistributionItem[];
    widthLoading?: boolean;
    /** Unfiltered distributions for pie charts (so all segments stay visible) */
    chartDistributions?: {
        color?: Stats["color_distribution"];
        height?: Stats["height_distribution"];
        depth?: Stats["depth_distribution"];
        frame_type?: Stats["frame_type_distribution"];
    };
    crossFilters: {
        color?: string;
        height_cm?: string;
        depth_cm?: string;
        width_cm?: number;
    };
    /** Currently selected frame type categories */
    frameTypes?: string[];
    /** All available frame type categories for the current anchor */
    frameTypeOptions?: string[];
    onColorClick: (value: string) => void;
    onHeightClick: (value: string) => void;
    onDepthClick: (value: string) => void;
    onWidthClick: (widthCm: number | undefined) => void;
    onFrameTypeClick?: (types: string[]) => void;
}

export function FramesDetails({
    stats,
    widthDistribution,
    widthLoading,
    chartDistributions,
    crossFilters,
    frameTypes,
    frameTypeOptions,
    onColorClick,
    onHeightClick,
    onDepthClick,
    onWidthClick,
    onFrameTypeClick,
}: FramesDetailsProps) {
    const supportsHeightExtensions = frameTypeOptions?.includes("height_extension") ?? (stats.anchor === "PAX");
    // Each pie uses filtered stats (reacts to other dimensions) UNLESS its own
    // dimension is filtered — then it uses the unfiltered pieBase so all segments
    // stay visible for selection.
    const colorData = useMemo(() => {
        const source = crossFilters.color
            ? (chartDistributions?.color ?? stats.color_distribution ?? [])
            : (stats.color_distribution ?? []);
        return source.map((d) => ({ label: d.color || "Unknown", value: d.count }));
    }, [stats.color_distribution, chartDistributions?.color, crossFilters.color]);

    const heightData = useMemo(() => {
        const fmt = (v: number | string | undefined) => (v === "Mixed" ? "Mixed" : v != null ? `${v}cm` : "Unknown");
        const source = crossFilters.height_cm
            ? (chartDistributions?.height ?? stats.height_distribution ?? [])
            : (stats.height_distribution ?? []);
        return source.map((d) => ({ label: fmt(d.height_cm), value: d.count }));
    }, [stats.height_distribution, chartDistributions?.height, crossFilters.height_cm]);

    const depthData = useMemo(() => {
        const fmt = (v: number | string | undefined) => (v === "Mixed" ? "Mixed" : v != null ? `${v}cm` : "Unknown");
        const source = crossFilters.depth_cm
            ? (chartDistributions?.depth ?? stats.depth_distribution ?? [])
            : (stats.depth_distribution ?? []);
        return source.map((d) => ({ label: fmt(d.depth_cm), value: d.count }));
    }, [stats.depth_distribution, chartDistributions?.depth, crossFilters.depth_cm]);

    // Map ProductType → category name (for filter coupling)
    const FRAME_TYPE_TO_CATEGORY: Record<string, string> = {
        // PAX
        "wardrobe frame": "regular",
        "wall-mounted storage frame": "regular",
        "wall-mounted storage frame w rail": "regular",
        "add-on corner unit": "corner",
        "add-on corner unit with 4 shelves": "corner",
        "corner section frame": "corner",
        "add-on end-unit": "end",
        "add-on mid-unit": "mid",
        "height extension unit": "height_extension",
        // BESTÅ
        "frame": "regular",
        "shelf unit": "regular",
        "shelf unit/height extension unit": "regular",
        "TV bench": "tv",
        "TV storage unit": "tv",
        "wall shelf": "regular",
        "wall shelf unit": "regular",
    };
    const CATEGORY_LABELS: Record<string, string> = {
        regular: "Regular",
        corner: "Corner",
        end: "End unit",
        mid: "Mid unit",
        height_extension: "Height extension",
        tv: "TV bench",
        wall: "Wall-mounted",
    };

    // Aggregate raw frame types into categories for the pie chart.
    // Uses unfiltered chartDistributions when available so all segments
    // keep their real values even when frame_types filter is active.
    // Pre-seeds with the anchor's categories so all slices are always visible.
    const frameTypeData = useMemo(() => {
        const source = chartDistributions?.frame_type ?? stats.frame_type_distribution ?? [];
        const categoryTotals = new Map<string, number>(
            (frameTypeOptions || []).map(k => [k, 0])
        );
        for (const d of source) {
            const cat = FRAME_TYPE_TO_CATEGORY[d.frame_type] || d.frame_type;
            categoryTotals.set(cat, (categoryTotals.get(cat) || 0) + d.count);
        }
        return [...categoryTotals.entries()].map(([cat, count]) => ({
            label: CATEGORY_LABELS[cat] || cat,
            value: count,
        }));
    }, [stats.frame_type_distribution, chartDistributions?.frame_type]);

    // Build selectedValue for the pie chart from frameTypes prop
    const frameTypeSelectedValue = useMemo(() => {
        if (!frameTypes || (frameTypeOptions && frameTypes.length === frameTypeOptions.length)) return undefined;
        if (!frameTypeOptions && frameTypes.length === ALL_FRAME_TYPES.length) return undefined;
        return frameTypes.map(t => CATEGORY_LABELS[t] || t).join(",");
    }, [frameTypes, frameTypeOptions]);

    // Pagination state
    const [widthPageSize, setWidthPageSize] = useState(15);
    const [widthPage, setWidthPage] = useState(1);
    const [frameCompPageSize, setFrameCompPageSize] = useState(15);
    const [frameCompPage, setFrameCompPage] = useState(1);

    // Auto-navigate to page containing selected width when data changes
    useEffect(() => {
        if (crossFilters.width_cm != null) {
            const idx = widthDistribution.findIndex((w) => w.width_cm === crossFilters.width_cm);
            if (idx >= 0) setWidthPage(Math.floor(idx / widthPageSize) + 1);
        }
    }, [widthDistribution, crossFilters.width_cm, widthPageSize]);

    // Frame layout filter state
    const [selectedLayout, setSelectedLayout] = useState<string | null>(null);

    const layoutItems = stats.frame_layout_distribution || [];

    // Reset layout filter when the underlying data changes
    useEffect(() => {
        setSelectedLayout(null);
        setFrameCompPage(1);
    }, [stats.frame_layout_distribution]);

    // Filter frame compositions by selected layout
    const filteredFrameCompItems = useMemo(() => {
        const items = stats.frame_composition_distribution || [];
        if (!selectedLayout) return items;
        return items.filter((comp) => comp.layout === selectedLayout);
    }, [stats.frame_composition_distribution, selectedLayout]);

    // Frame compositions total (of filtered items)
    const frameCompTotal = useMemo(() => {
        return filteredFrameCompItems.reduce((sum, f) => sum + f.count, 0);
    }, [filteredFrameCompItems]);

    const frameCompTotalPct = useMemo(() => {
        return filteredFrameCompItems.reduce((sum, f) => sum + f.percentage, 0);
    }, [filteredFrameCompItems]);

    // Width table total
    const widthTotal = useMemo(() => {
        return widthDistribution.reduce((sum, w) => sum + w.count, 0);
    }, [widthDistribution]);

    const widthTotalPct = useMemo(() => {
        return widthDistribution.reduce((sum, w) => sum + (w.percentage || 0), 0);
    }, [widthDistribution]);

    const widthItems = widthDistribution;
    const widthPaged = widthItems.slice((widthPage - 1) * widthPageSize, widthPage * widthPageSize);

    const frameCompPaged = filteredFrameCompItems.slice((frameCompPage - 1) * frameCompPageSize, frameCompPage * frameCompPageSize);

    return (
        <div className={styles.container}>
            {/* Single row: Width table | Pie charts | Frame Combinations table */}
            <div className={styles.mainRow}>
                {/* Left: Width distribution table */}
                <div className={`${styles.widthPanel} ${widthLoading && widthDistribution.length === 0 ? styles.stale : ''}`}>
                    <h3 className={styles.panelTitle} title={supportsHeightExtensions ? "Horizontal width of base frames; stacked height extensions excluded" : "Total wardrobe width of the combination (sum of all frames)"}>Total Width</h3>
                    <p className={styles.hint}>Click to filter</p>
                    {widthDistribution.length === 0 ? (
                        <div className={styles.loading}>{widthLoading ? "Loading…" : "No base-frame width available"}</div>
                    ) : (
                    <table className={styles.widthTable}>
                        <thead>
                            <tr>
                                <th>Width</th>
                                <th>
                                    <HelpHint text={COMBO_QUANTITY_COL}>
                                        Receipts
                                    </HelpHint>
                                </th>
                                <th>
                                    <HelpHint text={SHARE_PCT_COL}>
                                        %
                                    </HelpHint>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {widthPaged.map((w) => {
                                const isSelected = crossFilters.width_cm === w.width_cm;
                                const isDimmed = crossFilters.width_cm != null && !isSelected;
                                return (
                                    <tr
                                        key={w.width_cm}
                                        className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""} ${isDimmed ? styles.dimmedRow : ""}`}
                                        onClick={() => onWidthClick(isSelected ? undefined : w.width_cm)}
                                    >
                                        <td>{w.width_cm}cm</td>
                                        <td>{w.count.toLocaleString()}</td>
                                        <td>{w.percentage?.toFixed(1) || 0}%</td>
                                    </tr>
                                );
                            })}
                            <tr className={styles.totalRow}>
                                <td><strong>Total</strong></td>
                                <td><strong>{widthTotal.toLocaleString()}</strong></td>
                                <td><strong>{widthTotalPct.toFixed(1)}%</strong></td>
                            </tr>
                        </tbody>
                    </table>
                    )}
                    {widthDistribution.length > 0 && (
                    <Pagination currentPage={widthPage} totalItems={widthItems.length} pageSize={widthPageSize} onPageChange={setWidthPage} onPageSizeChange={(s) => { setWidthPageSize(s); setWidthPage(1); }} pageSizeOptions={[10, 15, 30]} />
                    )}
                </div>

                {/* Middle: Pie charts that wrap if tight */}
                <div className={styles.chartsMiddle}>
                    <DistributionChart
                        title="Frame colour"
                        data={colorData}
                        selectedValue={crossFilters.color?.split(",").map(v => v === "mixed" ? "Mixed" : v).join(",")}
                        onSegmentClick={(color, multi) =>
                            onColorClick(
                                toggleInSet(
                                    crossFilters.color,
                                    colourValue(color),
                                    colorData.map((d) => colourValue(d.label)),
                                    multi,
                                ),
                            )
                        }
                        formatLabel={normalizeColourSpelling}
                        matchLabel={colourMatchKey}
                        legendPosition="bottom"
                        showTotal={true}
                    />
                    <DistributionChart
                        title="Frame height"
                        helpText={supportsHeightExtensions ? "Base-frame height only. Height extensions appear separately in Frame Combinations; assembled height cannot be inferred from receipt items." : undefined}
                        data={heightData}
                        selectedValue={crossFilters.height_cm ? crossFilters.height_cm.split(",").map(v => v === "mixed" ? "Mixed" : `${v}cm`).join(",") : undefined}
                        onSegmentClick={(h, multi) =>
                            onHeightClick(
                                toggleInSet(crossFilters.height_cm, cmValue(h), heightData.map((d) => cmValue(d.label)), multi),
                            )
                        }
                        legendPosition="bottom"
                        showTotal={true}
                    />
                    <DistributionChart
                        title="Frame depth"
                        data={depthData}
                        selectedValue={crossFilters.depth_cm ? crossFilters.depth_cm.split(",").map(v => v === "mixed" ? "Mixed" : `${v}cm`).join(",") : undefined}
                        onSegmentClick={(d, multi) =>
                            onDepthClick(
                                toggleInSet(crossFilters.depth_cm, cmValue(d), depthData.map((x) => cmValue(x.label)), multi),
                            )
                        }
                        legendPosition="bottom"
                        showTotal={true}
                    />
                    {frameTypeData.length > 0 && frameTypes && onFrameTypeClick && (
                    <DistributionChart
                        title="Frame type"
                        data={frameTypeData}
                        selectedValue={frameTypeSelectedValue}
                        onSegmentClick={(label, multi) => {
                            if (!onFrameTypeClick || !frameTypes) return;
                            const LABEL_TO_CATEGORY: Record<string, string> = { Regular: "regular", Corner: "corner", "End unit": "end", "Mid unit": "mid", "Height extension": "height_extension", "TV bench": "tv", "Wall-mounted": "wall" };
                            const cat = LABEL_TO_CATEGORY[label];
                            if (!cat) return;
                            const allTypes = frameTypeOptions || ALL_FRAME_TYPES;
                            if (multi) {
                                const set = new Set(frameTypes);
                                if (set.has(cat)) {
                                    set.delete(cat);
                                    onFrameTypeClick(set.size > 0 ? [...set] : allTypes);
                                } else {
                                    set.add(cat);
                                    onFrameTypeClick([...set]);
                                }
                            } else {
                                // Regular click: exclusive select (or deselect → all)
                                const isOnlySelected = frameTypes.length === 1 && frameTypes[0] === cat;
                                onFrameTypeClick(isOnlySelected ? allTypes : [cat]);
                            }
                        }}
                        legendPosition="bottom"
                        showTotal={true}
                        totalOverride={stats.total_combinations}
                    />
                    )}
                </div>

                {/* Right: Frame combinations */}
                <div className={`${styles.frameNamesPanel} ${widthLoading && filteredFrameCompItems.length === 0 ? styles.stale : ''}`}>
                    <h3 className={styles.panelTitle} title="Frame layout patterns in customer combinations">Frame Combinations</h3>
                    <p className={styles.hint}>Frame configurations customers buy{supportsHeightExtensions ? ". Height extensions listed separately; width layout excludes stacked extensions." : ""}</p>
                    {layoutItems.length > 1 && (
                        <select
                            className={styles.layoutSelect}
                            value={selectedLayout || ""}
                            onChange={(e) => { setSelectedLayout(e.target.value || null); setFrameCompPage(1); }}
                        >
                            <option value="">All layouts</option>
                            {layoutItems.map((l) => {
                                const pct = l.percentage;
                                const pctStr = pct >= 1
                                    ? `${pct.toFixed(0)}%`
                                    : pct <= 0
                                        ? '<0.01%'
                                        : `${pct.toFixed(Math.max(1, Math.ceil(-Math.log10(pct)) + 1))}%`;
                                return (
                                    <option key={l.description} value={l.description}>
                                        {l.description.split(" + ").join(" + ").split(" x ").join("× ")} ({l.count.toLocaleString()} · {pctStr})
                                    </option>
                                );
                            })}
                        </select>
                    )}
                    {filteredFrameCompItems.length === 0 ? (
                        <div className={styles.loading}>Loading…</div>
                    ) : (
                    <div className={styles.frameNamesTableWrapper}>
                        <table className={styles.frameNamesTable}>
                            <thead>
                                <tr>
                                    <th className={styles.rankCol}>#</th>
                                    <th className={styles.nameCol}>Configuration</th>
                                    <th className={styles.countCol}>
                                        <HelpHint text={COMBO_QUANTITY_COL}>
                                            Receipts
                                        </HelpHint>
                                    </th>
                                    <th className={styles.pctCol}>
                                        <HelpHint text={SHARE_PCT_COL}>
                                            %
                                        </HelpHint>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {frameCompPaged.map((comp, idx) => (
                                    <tr key={`${comp.description}-${idx}`}>
                                        <td className={styles.rankCell}>{(frameCompPage - 1) * frameCompPageSize + idx + 1}.</td>
                                        <td className={styles.nameCell}>
                                            <ComboDescription text={comp.description.split(" + ").join("\n").split(" x ").join("× ")} itemNoLookup={stats.frame_desc_item_nos} replacementsLookup={stats.frame_desc_replacements} />
                                        </td>
                                        <td className={styles.countCell}>
                                            {comp.count.toLocaleString()}
                                        </td>
                                        <td className={styles.pctCell}>
                                            {comp.percentage.toFixed(1)}%
                                        </td>
                                    </tr>
                                ))}
                                <tr className={styles.totalRow}>
                                    <td></td>
                                    <td className={styles.totalLabel}><strong>Total</strong></td>
                                    <td className={styles.countCell}>
                                        <strong>{frameCompTotal.toLocaleString()}</strong>
                                    </td>
                                    <td className={styles.pctCell}><strong>{frameCompTotalPct.toFixed(1)}%</strong></td>
                                </tr>
                            </tbody>
                        </table>
                        <Pagination currentPage={frameCompPage} totalItems={filteredFrameCompItems.length} pageSize={frameCompPageSize} onPageChange={setFrameCompPage} onPageSizeChange={(s) => { setFrameCompPageSize(s); setFrameCompPage(1); }} pageSizeOptions={[10, 15, 30]} />
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
}
